import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Search, FileText, LayoutGrid, List, Settings, Ban, SlidersHorizontal, ChevronDown, ChevronRight, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useBusiness } from '../contexts/BusinessContext';
import apiService from '../services/api';
import CustomAlertModal from './CustomAlertModal';
import MarkInvoicePaidModal from './MarkInvoicePaidModal';
import { formatDateOnly } from '../utils/dateOnly';
import { getInvoiceAmountDue } from '../utils/invoiceAmountDue';

type InvoiceStatus = 'DRAFT' | 'SENT' | 'PAID' | 'VOIDED';
type InvoiceSource = 'booking' | 'manual';
type DatePreset = 'last30' | 'last7' | 'thisMonth' | 'last90' | 'custom';

type InvoiceRow = {
  id: string;
  business_id: string;
  appointment_id?: string | null;
  bill_to: string;
  customer_email?: string | null;
  invoice_date: string;
  due_date: string;
  status: InvoiceStatus;
  sub_total: string | number;
  discount: string | number;
  tax: string | number;
  tip?: string | number;
  total: string | number;
  deposit?: string | number;
  /** Amount still owed (e.g. total minus deposit); when absent, fall back to total for display */
  due?: string | number;
  created_at?: string;
  updated_at?: string;
  status_at?: string | null;
  sent_at?: string | null;
  last_resent_at?: string | null;
  paid_at?: string | null;
  amount_due?: number;
};

const safeNum = (v: any): number => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
  return Number.isFinite(n) ? n : 0;
};

const formatMoney = (v: any): string => {
  const n = typeof v === 'number' ? v : parseFloat(String(v ?? '0'));
  const safe = Number.isFinite(n) ? n : 0;
  return safe.toFixed(2);
};

const formatDate = (v: any): string => formatDateOnly(v, undefined, { year: 'numeric', month: 'numeric', day: 'numeric' });

const toLocalYmd = (d: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const addDays = (d: Date, deltaDays: number): Date => {
  const x = new Date(d);
  x.setDate(x.getDate() + deltaDays);
  return x;
};

const statusPill = (status: InvoiceStatus) => {
  switch (status) {
    case 'DRAFT':
      return 'bg-gray-100 text-gray-700 border-gray-200';
    case 'SENT':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'PAID':
      return 'bg-green-50 text-green-700 border-green-200';
    case 'VOIDED':
      return 'bg-red-50 text-red-700 border-red-200';
    default:
      return 'bg-gray-100 text-gray-700 border-gray-200';
  }
};

const formatStatusLabel = (status: string): string =>
  (status || '').split(/\s+/).map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');

const formatStatusAging = (statusAt: string | null | undefined): string => {
  if (!statusAt) return '';
  const date = new Date(statusAt);
  if (!Number.isFinite(date.getTime())) return '';

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const statusDay = new Date(date);
  statusDay.setHours(0, 0, 0, 0);

  const diffDays = Math.floor((today.getTime() - statusDay.getTime()) / (1000 * 60 * 60 * 24));
  const mmddyyyy = date.toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
  });

  if (diffDays === 0) return `On ${mmddyyyy}`;
  if (diffDays === 1) return '1 day ago';
  return `${diffDays} days ago`;
};

const StatusBadgeWithAging: React.FC<{ status: InvoiceStatus; statusAt?: string | null }> = ({
  status,
  statusAt,
}) => {
  const aging = formatStatusAging(statusAt);
  return (
    <div className="inline-flex flex-col items-start gap-1">
      <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-xs font-semibold ${statusPill(status)}`}>
        {formatStatusLabel(status)}
      </span>
      {aging ? <span className="text-xs text-gray-500">{aging}</span> : null}
    </div>
  );
};

const toInvoiceLabel = (id: string): string => {
  const short = (id || '').split('-')[0]?.toUpperCase() || '—';
  return `INV-${short}`;
};

const InvoiceNumberLink: React.FC<{ invoiceId: string; className?: string }> = ({ invoiceId, className = '' }) => (
  <Link
    to={`/dashboard/business/invoices/${invoiceId}`}
    state={{ from: '/dashboard/business?section=invoices' }}
    className={`text-[#0B4778] no-underline hover:opacity-80 ${className}`}
    onClick={(e) => e.stopPropagation()}
  >
    {toInvoiceLabel(invoiceId)}
  </Link>
);

const fmtExportDate = (v: any): string =>
  formatDateOnly(v, 'en-US', { year: 'numeric', month: '2-digit', day: '2-digit' });

const escapeCsvCell = (value: unknown): string => {
  const s = String(value ?? '');
  if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const getInvoiceBaseAmount = (row: InvoiceRow): number =>
  Math.max(0, safeNum(row.sub_total) - safeNum(row.discount)) + safeNum(row.tax);

const formatCustomerInfo = (row: InvoiceRow): string => {
  const name = String(row.bill_to ?? '').trim();
  const email = String(row.customer_email ?? '').trim();
  if (name && email) return `${name} (${email})`;
  return name || email || '—';
};

const getExportPaidDate = (row: InvoiceRow): string => {
  if (row.paid_at) return fmtExportDate(row.paid_at);
  if (String(row.status || '').toUpperCase() === 'PAID') return fmtExportDate(row.updated_at ?? row.created_at);
  return fmtExportDate(null);
};

const buildBusinessInvoicesCsv = (invoices: InvoiceRow[]): string => {
  const headers = [
    'Invoice Number',
    'Due Date',
    'Customer',
    'Invoice Amount',
    'Tip Amount',
    'Total Due',
    'Status',
    'Invoice Sent',
    'Invoice Resent',
    'Invoice Paid',
  ];
  const lines = invoices.map((r) =>
    [
      toInvoiceLabel(r.id),
      fmtExportDate(r.due_date),
      formatCustomerInfo(r),
      formatMoney(getInvoiceBaseAmount(r)),
      formatMoney(r.tip),
      formatMoney(r.amount_due ?? getInvoiceAmountDue(r)),
      formatStatusLabel(String(r.status || '')),
      fmtExportDate(r.sent_at),
      fmtExportDate(r.last_resent_at),
      getExportPaidDate(r),
    ]
      .map(escapeCsvCell)
      .join(',')
  );
  return [headers.map(escapeCsvCell).join(','), ...lines].join('\r\n');
};

const downloadCsvFile = (csv: string, filename: string) => {
  const blob = new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
};

const InvoicesSection: React.FC = () => {
  const { currentBusiness } = useBusiness();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<InvoiceRow[]>([]);
  const [busyInvoiceId, setBusyInvoiceId] = useState<string | null>(null);
  const [onlinePaymentsEnabled, setOnlinePaymentsEnabled] = useState(false);

  const [isMobile, setIsMobile] = useState(false);
  const [invoiceView, setInvoiceView] = useState<'grid' | 'table'>(() => {
    try {
      if (typeof window !== 'undefined' && window.innerWidth < 640) return 'grid'; // <sm
      const saved = localStorage.getItem('invoicesView');
      return saved === 'grid' ? 'grid' : 'table';
    } catch {
      return 'table';
    }
  });

  const [search, setSearch] = useState('');
  const [isFilterSectionOpen, setIsFilterSectionOpen] = useState(false);
  const [status, setStatus] = useState<'ALL' | InvoiceStatus>('ALL');
  const [source, setSource] = useState<'ALL' | InvoiceSource>('ALL');
  const [datePreset, setDatePreset] = useState<DatePreset>('last30');
  const [customFrom, setCustomFrom] = useState<string>('');
  const [customTo, setCustomTo] = useState<string>('');

  const [limit] = useState(25);
  const [offset, setOffset] = useState(0);
  const [paginationTotal, setPaginationTotal] = useState<number>(0);
  const [actionsMenu, setActionsMenu] = useState<{ invoiceId: string; top: number; left: number } | null>(null);
  const [mobileActionsInvoiceId, setMobileActionsInvoiceId] = useState<string | null>(null);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);
  const [confirm, setConfirm] = useState<{ isOpen: boolean; action: 'VOID' | null; invoiceId?: string }>({
    isOpen: false,
    action: null,
    invoiceId: undefined,
  });
  const [markPaidModal, setMarkPaidModal] = useState<{ isOpen: boolean; invoiceId: string; amountDue: number } | null>(
    null
  );
  const [exporting, setExporting] = useState(false);

  // Detect mobile (<sm) and force grid view on mobile
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => {
    if (isMobile) setInvoiceView('grid');
  }, [isMobile]);

  // Load business Stripe account to know if online payments are enabled (for Process Payment visibility)
  useEffect(() => {
    if (!currentBusiness?.id) {
      setOnlinePaymentsEnabled(false);
      return;
    }
    let cancelled = false;
    apiService
      .getBusinessStripeAccount(currentBusiness.id)
      .then((data) => {
        if (!cancelled) setOnlinePaymentsEnabled(!!(data?.chargesEnabled));
      })
      .catch(() => {
        if (!cancelled) setOnlinePaymentsEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [currentBusiness?.id]);

  // Close actions dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const container = actionsMenuRef.current;
      if (container && container.contains(event.target as Node)) return;
      setActionsMenu(null);
    };

    if (actionsMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [actionsMenu]);

  // Close menu on scroll/resize to avoid mis-positioning
  useEffect(() => {
    if (!actionsMenu) return;
    const close = () => setActionsMenu(null);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [actionsMenu]);

  const { dateFrom, dateTo } = useMemo(() => {
    const today = new Date();
    const to = toLocalYmd(today);
    if (datePreset === 'custom') {
      return {
        dateFrom: customFrom.trim() || undefined,
        dateTo: customTo.trim() || undefined,
      };
    }
    if (datePreset === 'thisMonth') {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      return { dateFrom: toLocalYmd(start), dateTo: to };
    }
    if (datePreset === 'last7') {
      return { dateFrom: toLocalYmd(addDays(today, -6)), dateTo: to };
    }
    if (datePreset === 'last90') {
      return { dateFrom: toLocalYmd(addDays(today, -89)), dateTo: to };
    }
    // last30 default
    return { dateFrom: toLocalYmd(addDays(today, -29)), dateTo: to };
  }, [datePreset, customFrom, customTo]);

  const datePresetLabel = useMemo(() => {
    switch (datePreset) {
      case 'last7':
        return 'Last 7 days';
      case 'thisMonth':
        return 'This month';
      case 'last90':
        return 'Last 90 days';
      case 'custom':
        return 'Custom range...';
      case 'last30':
      default:
        return 'Last 30 days';
    }
  }, [datePreset]);

  const load = useCallback(async () => {
    if (!currentBusiness?.id) return;
    setLoading(true);
    try {
      const resp = await apiService.getInvoices({
        businessId: currentBusiness.id,
        status: status === 'ALL' ? undefined : status,
        source: source === 'ALL' ? undefined : source,
        search: search.trim() || undefined,
        dateFrom,
        dateTo,
        limit,
        offset,
      });
      setRows(resp.data as any);
      setPaginationTotal(parseInt(String(resp.pagination?.total ?? '0'), 10) || 0);
    } catch (e) {
      console.error('Error loading invoices:', e);
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [currentBusiness?.id, status, source, search, dateFrom, dateTo, limit, offset]);

  useEffect(() => {
    load();
  }, [load]);

  // reset paging when filters change
  useEffect(() => {
    setOffset(0);
  }, [status, source, datePreset, customFrom, customTo, search]);

  const page = useMemo(() => Math.floor(offset / limit) + 1, [offset, limit]);
  const totalPages = useMemo(() => Math.max(1, Math.ceil((paginationTotal || 0) / limit)), [paginationTotal, limit]);

  useEffect(() => {
    // Don't overwrite desktop preference while mobile forces grid.
    if (isMobile) return;
    try {
      localStorage.setItem('invoicesView', invoiceView);
    } catch {}
  }, [invoiceView, isMobile]);

  const navigate = useNavigate();

  const selectedInvoice = useMemo(() => {
    const id = actionsMenu?.invoiceId || confirm.invoiceId || mobileActionsInvoiceId;
    if (!id) return null;
    return rows.find((r) => r.id === id) || null;
  }, [rows, actionsMenu?.invoiceId, confirm.invoiceId, mobileActionsInvoiceId]);

  const downloadPdf = async (invoiceId: string) => {
    try {
      const t = toast.loading('Preparing PDF…');
      const { blob, filename } = await apiService.downloadInvoicePdf(invoiceId);

      const label = toInvoiceLabel(invoiceId).replace(/[^\w-]/g, '_');
      const fallbackName = `Invoice_${label}.pdf`;
      const finalName = (filename || fallbackName).replace(/[\\/:*?"<>|]+/g, '_');

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = finalName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);

      toast.dismiss(t);
      toast.success('Invoice PDF downloaded');
    } catch (e) {
      console.error('Failed to download invoice PDF:', e);
      toast.error('Failed to download PDF');
    }
  };

  const handleExport = useCallback(async () => {
    if (!currentBusiness?.id) return;
    setExporting(true);
    const toastId = toast.loading('Preparing export…');
    try {
      const sharedFilters = {
        businessId: currentBusiness.id,
        status: status === 'ALL' ? undefined : status,
        source: source === 'ALL' ? undefined : source,
        search: search.trim() || undefined,
        dateFrom,
        dateTo,
      };
      const pageSize = 100;
      let offsetCursor = 0;
      let total = Infinity;
      const allRows: InvoiceRow[] = [];

      while (offsetCursor < total) {
        const resp = await apiService.getInvoices({
          ...sharedFilters,
          limit: pageSize,
          offset: offsetCursor,
        });
        const batch = (resp.data ?? []) as InvoiceRow[];
        allRows.push(...batch);
        total = safeNum(resp.pagination?.total) || allRows.length;
        offsetCursor += pageSize;
        if (batch.length === 0) break;
      }

      if (allRows.length === 0) {
        toast.error('No invoices to export for the current filters', { id: toastId });
        return;
      }

      const csv = buildBusinessInvoicesCsv(allRows);
      const stamp = new Date().toISOString().slice(0, 10);
      const businessSlug = String(currentBusiness.name || 'business')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      downloadCsvFile(csv, `invoices-${businessSlug || 'export'}-${stamp}.csv`);
      toast.success(`Exported ${allRows.length} invoice${allRows.length === 1 ? '' : 's'}`, { id: toastId });
    } catch (e: any) {
      console.error('Failed to export invoices', e);
      toast.error(e?.response?.data?.error || 'Failed to export invoices', { id: toastId });
    } finally {
      setExporting(false);
    }
  }, [currentBusiness?.id, currentBusiness?.name, dateFrom, dateTo, search, source, status]);

  const resendInvoice = async (invoiceId: string) => {
    const row = rows.find((r) => r.id === invoiceId);
    if (!row) return;
    if (!String(row.customer_email || '').trim()) {
      toast.error('No customer email on file for this invoice');
      return;
    }
    setBusyInvoiceId(invoiceId);
    const t = toast.loading('Sending invoice…');
    try {
      await apiService.sendInvoiceEmail(invoiceId);
      toast.dismiss(t);
      toast.success('Invoice email sent');
      await load();
    } catch (e: any) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.error || 'Failed to send invoice email');
    } finally {
      setBusyInvoiceId(null);
    }
  };

  const markAsPaid = async (
    invoiceId: string,
    payment: {
      paymentMethod: string;
      paymentDate: string;
      referenceNumber?: string | null;
      notes?: string | null;
    }
  ) => {
    setBusyInvoiceId(invoiceId);
    const t = toast.loading('Marking as paid…');
    try {
      await apiService.markInvoicePaid(invoiceId, payment);
      toast.dismiss(t);
      toast.success('Invoice marked as paid');
      await load();
    } catch (e: any) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.error || 'Failed to mark invoice as paid');
      throw e;
    } finally {
      setBusyInvoiceId(null);
    }
  };

  const voidInvoice = async (invoiceId: string) => {
    setBusyInvoiceId(invoiceId);
    const t = toast.loading('Voiding invoice…');
    try {
      await apiService.updateInvoice(invoiceId, { status: 'VOIDED' });
      toast.dismiss(t);
      toast.success('Invoice voided');
      await load();
    } catch (e: any) {
      toast.dismiss(t);
      toast.error(e?.response?.data?.error || 'Failed to update invoice');
    } finally {
      setBusyInvoiceId(null);
    }
  };

  const handleInvoiceAction = async (action: string, invoiceId?: string) => {
    const id = invoiceId ?? actionsMenu?.invoiceId;
    setActionsMenu(null);
    setMobileActionsInvoiceId(null);
    if (!id) return;

    if (action === 'Edit' || action === 'View' || action === 'View Details') {
      navigate(`/dashboard/business/invoices/${id}`, { state: { from: '/dashboard/business?section=invoices' } });
      return;
    }
    if (action === 'Download PDF') {
      await downloadPdf(id);
      return;
    }
    if (action === 'Resend') {
      await resendInvoice(id);
      return;
    }
    if (action === 'Process Payment') {
      navigate(`/dashboard/business/invoices/${id}/charge`, { state: { from: '/dashboard/business?section=invoices' } });
      return;
    }
    if (action === 'Mark as Paid') {
      const row = rows.find((r) => r.id === id);
      if (!row) return;
      setMarkPaidModal({
        isOpen: true,
        invoiceId: id,
        amountDue: getInvoiceAmountDue(row),
      });
      return;
    }
    if (action === 'Void') {
      setConfirm({ isOpen: true, action: 'VOID', invoiceId: id });
      return;
    }
  };

  const renderInvoiceActionButtons = (invoiceId: string, showChevrons = false) => {
    const invStatus = (rows.find((r) => r.id === invoiceId)?.status || '').toUpperCase();
    const isView = invStatus === 'VOIDED' || invStatus === 'PAID';
    const showResendAndVoid = invStatus !== 'VOIDED' && invStatus !== 'PAID';
    const chevron = showChevrons ? <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" /> : null;

    return (
      <>
        <button
          type="button"
          onClick={() => handleInvoiceAction(isView ? 'View' : 'View Details', invoiceId)}
          className="w-full text-left px-4 py-3 text-sm text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-2"
        >
          <i className="bi bi-eye"></i>
          <span className="flex-1">{isView ? 'View' : 'View Details'}</span>
          {chevron}
        </button>
        <div className="border-t border-gray-100 my-1" />
        <button
          type="button"
          onClick={() => handleInvoiceAction('Download PDF', invoiceId)}
          className="w-full text-left px-4 py-3 text-sm text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-2"
        >
          <i className="bi bi-download"></i>
          <span className="flex-1">Download PDF</span>
          {chevron}
        </button>
        {onlinePaymentsEnabled && invStatus !== 'VOIDED' && (
          <>
            <div className="border-t border-gray-100 my-1" />
            <button
              type="button"
              onClick={() => handleInvoiceAction('Process Payment', invoiceId)}
              className="w-full text-left px-4 py-3 text-sm text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={busyInvoiceId === invoiceId || invStatus === 'PAID'}
            >
              <i className="bi bi-credit-card text-green-600"></i>
              <span className="flex-1">Process Payment</span>
              {chevron}
            </button>
          </>
        )}
        {showResendAndVoid && (
          <>
            <div className="border-t border-gray-100 my-1" />
            <button
              type="button"
              onClick={() => handleInvoiceAction('Mark as Paid', invoiceId)}
              className="w-full text-left px-4 py-3 text-sm text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={busyInvoiceId === invoiceId}
            >
              <i className="bi bi-cash-coin text-green-600"></i>
              <span className="flex-1">Mark as Paid</span>
              {chevron}
            </button>
            <button
              type="button"
              onClick={() => handleInvoiceAction('Resend', invoiceId)}
              className="w-full text-left px-4 py-3 text-sm text-gray-900 hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={busyInvoiceId === invoiceId}
            >
              <i className="bi bi-send text-amber-600"></i>
              <span className="flex-1">Resend</span>
              {chevron}
            </button>
            <div className="border-t border-gray-100 my-1" />
            <button
              type="button"
              onClick={() => handleInvoiceAction('Void', invoiceId)}
              className="w-full text-left px-4 py-3 text-sm text-red-600 hover:bg-gray-50 transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={busyInvoiceId === invoiceId}
            >
              <i className="bi bi-slash-circle"></i>
              <span className="flex-1">Void</span>
              {chevron}
            </button>
          </>
        )}
      </>
    );
  };

  const openActionsFor = (invoiceId: string, anchor: HTMLElement) => {
    const rect = anchor.getBoundingClientRect();
    const menuWidth = 224; // w-56
    const menuHeight = 320; // rough height for placement
    const padding = 8;

    const preferDown = rect.bottom + 6 + menuHeight <= window.innerHeight - padding;
    const top = preferDown ? rect.bottom + 6 : Math.max(padding, rect.top - menuHeight - 6);

    // Right-align menu to the button
    let left = rect.right - menuWidth;
    left = Math.max(padding, Math.min(left, window.innerWidth - menuWidth - padding));

    setActionsMenu({ invoiceId, top, left });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h2 className="hidden md:block text-xl md:text-2xl font-bold text-gray-900">Invoices</h2>
          <p className="text-gray-600 mt-1">Create and manage invoices, and track status.</p>
        </div>
        <div className="flex gap-3 flex-nowrap w-full sm:w-auto">
          <button
            type="button"
            className="aacore-btn2-secondary flex-1 sm:flex-none"
            onClick={handleExport}
            disabled={exporting || loading || !currentBusiness?.id}
            title="Export invoices to Excel (CSV)"
          >
            <i className="bi bi-download"></i>
            {exporting ? 'Exporting…' : 'Export'}
          </button>
          <button
            type="button"
            className="aacore-btn2-primary flex-1 sm:flex-none"
            onClick={() => navigate('/dashboard/business/invoices/create', { state: { from: '/dashboard/business?section=invoices' } })}
          >
            <i className="bi bi-plus-lg"></i>
            New Invoice
          </button>
        </div>
      </div>

      {/* Mobile: collapsible filters */}
      <div className="md:hidden">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <button
            type="button"
            onClick={() => setIsFilterSectionOpen(!isFilterSectionOpen)}
            className="w-full flex items-center justify-between px-4 py-3 bg-[#0B4778] text-white hover:bg-[#093a63] transition-colors"
            aria-expanded={isFilterSectionOpen}
          >
            <span className="flex items-center gap-2 text-sm font-semibold">
              <SlidersHorizontal className="w-4 h-4" />
              Filters
            </span>
            <span className="flex items-center gap-2 text-sm font-medium">
              {paginationTotal} invoice{paginationTotal !== 1 ? 's' : ''}
              <ChevronDown className={`w-5 h-5 text-white/90 transition-transform ${isFilterSectionOpen ? 'rotate-180' : ''}`} />
            </span>
          </button>

          {isFilterSectionOpen && (
            <div className="p-4 space-y-3 border-t border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search invoice #, customer, email..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className={`w-full pl-10 ${search ? 'pr-10' : 'pr-3'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]`}
                />
                {search && (
                  <button
                    type="button"
                    onClick={() => setSearch('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                    aria-label="Clear search"
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                )}
              </div>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value as any)}
                className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white text-gray-900"
                aria-label="Filter by status"
              >
                <option value="ALL">All Status</option>
                <option value="DRAFT">Draft</option>
                <option value="SENT">Sent</option>
                <option value="PAID">Paid</option>
              </select>
              <select
                value={source}
                onChange={(e) => setSource(e.target.value as any)}
                className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white text-gray-900"
                aria-label="Filter by source"
              >
                <option value="ALL">All Sources</option>
                <option value="booking">From booking</option>
                <option value="manual">Manual</option>
              </select>
              <select
                value={datePreset}
                onChange={(e) => {
                  const next = e.target.value as DatePreset;
                  setDatePreset(next);
                  if (next !== 'custom') {
                    setCustomFrom('');
                    setCustomTo('');
                  }
                }}
                className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white text-gray-900"
                aria-label="Filter by date"
                title={datePresetLabel}
              >
                <option value="last30">Last 30 days</option>
                <option value="last7">Last 7 days</option>
                <option value="thisMonth">This month</option>
                <option value="last90">Last 90 days</option>
                <option value="custom">Custom range...</option>
              </select>
              {datePreset === 'custom' && (
                <div className="grid grid-cols-1 gap-3 pt-1">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">From</label>
                    <input
                      type="date"
                      value={customFrom}
                      onChange={(e) => setCustomFrom(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-1">To</label>
                    <input
                      type="date"
                      value={customTo}
                      onChange={(e) => setCustomTo(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white"
                    />
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Desktop: always-visible filters */}
      <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative min-w-[12rem] flex-1 basis-[16rem] max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search invoice #, customer, email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={`w-full pl-10 ${search ? 'pr-10' : 'pr-3'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778]`}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 focus:outline-none"
                aria-label="Clear search"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            )}
          </div>

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as any)}
            className="min-w-[9.5rem] flex-none pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white text-gray-900"
            aria-label="Filter by status"
          >
            <option value="ALL">All Status</option>
            <option value="DRAFT">Draft</option>
            <option value="SENT">Sent</option>
            <option value="PAID">Paid</option>
          </select>

          <select
            value={source}
            onChange={(e) => setSource(e.target.value as any)}
            className="min-w-[9.5rem] flex-none pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white text-gray-900"
            aria-label="Filter by source"
          >
            <option value="ALL">All Sources</option>
            <option value="booking">From booking</option>
            <option value="manual">Manual</option>
          </select>

          <select
            value={datePreset}
            onChange={(e) => {
              const next = e.target.value as DatePreset;
              setDatePreset(next);
              if (next !== 'custom') {
                setCustomFrom('');
                setCustomTo('');
              }
            }}
            className="min-w-[10rem] flex-none pl-3 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white text-gray-900"
            aria-label="Filter by date"
            title={datePresetLabel}
          >
            <option value="last30">Last 30 days</option>
            <option value="last7">Last 7 days</option>
            <option value="thisMonth">This month</option>
            <option value="last90">Last 90 days</option>
            <option value="custom">Custom range...</option>
          </select>

          <div className="flex gap-2 flex-none ml-auto">
            <button
              type="button"
              onClick={() => setInvoiceView('grid')}
              title="Grid View"
              className={`px-3 py-2 rounded-lg text-sm flex items-center justify-center ${
                invoiceView === 'grid' ? 'bg-[#0B4778] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
            <button
              type="button"
              onClick={() => setInvoiceView('table')}
              title="List View"
              className={`px-3 py-2 rounded-lg text-sm flex items-center justify-center ${
                invoiceView === 'table' ? 'bg-[#0B4778] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {datePreset === 'custom' && (
        <div className="hidden md:block bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">From</label>
                <input
                  type="date"
                  value={customFrom}
                  onChange={(e) => setCustomFrom(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-1">To</label>
                <input
                  type="date"
                  value={customTo}
                  onChange={(e) => setCustomTo(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0B4778] focus:border-[#0B4778] bg-white"
                />
              </div>
            </div>
            <div className="text-sm text-gray-500">
              Tip: leave one side blank to filter by only a start or end date.
            </div>
          </div>
        </div>
      )}

      {/* Results */}
      {invoiceView === 'grid' ? (
        <div className="max-md:bg-transparent max-md:border-0 max-md:shadow-none max-md:p-0 md:bg-white md:rounded-lg md:shadow-sm md:border md:border-gray-200 md:p-4">
          {loading ? (
            <div className="px-6 py-10 text-center text-gray-600">Loading invoices…</div>
          ) : rows.length === 0 ? (
            <div className="px-6 py-10 text-center">
              <div className="text-gray-500">
                <FileText className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                <div className="font-semibold text-gray-700">No invoices yet</div>
                <div className="text-sm text-gray-500 mt-1">Create an invoice from an appointment to see it here.</div>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {rows.map((r) => {
                const fromBooking = !!r.appointment_id;
                const isOpen = actionsMenu?.invoiceId === r.id;
                return (
                  <div key={r.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow bg-white relative">
                    <button
                      type="button"
                      className="md:hidden absolute inset-0 z-10 w-full h-full rounded-lg focus:outline-none focus:ring-2 focus:ring-[#0B4778] focus:ring-offset-1"
                      onClick={() => setMobileActionsInvoiceId(r.id)}
                      aria-label={`Invoice actions for ${toInvoiceLabel(r.id)}`}
                    />
                    <ChevronRight className="md:hidden absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none z-20" />
                    <div className="flex items-start justify-between gap-3 max-md:pr-8">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <InvoiceNumberLink invoiceId={r.id} className="font-extrabold relative z-20" />
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-extrabold ${
                              fromBooking ? 'bg-blue-50 border-blue-200 text-blue-700' : 'bg-slate-100 border-slate-200 text-slate-700'
                            }`}
                          >
                            <i className={`bi ${fromBooking ? 'bi-calendar2-check' : 'bi-pencil'}`}></i>
                            {fromBooking ? 'From booking' : 'Manual'}
                          </span>
                        </div>
                        <div className="text-sm text-gray-600 mt-1 truncate">{r.bill_to || 'Customer'}</div>
                        <div className="text-xs text-gray-500 truncate">{r.customer_email || 'No email on file'}</div>
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          const el = e.currentTarget as unknown as HTMLElement;
                          if (isOpen) setActionsMenu(null);
                          else openActionsFor(r.id, el);
                        }}
                        className="hidden md:flex p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors flex-shrink-0"
                        title="Actions"
                        aria-label="Invoice actions"
                      >
                        <Settings className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 items-center max-md:pr-8">
                      <div>
                        <div className="text-xs text-gray-500">DUE AMOUNT</div>
                        <div className="text-lg font-semibold text-gray-900">${formatMoney(r.due !== undefined && r.due !== null ? r.due : r.total)}</div>
                      </div>
                      <div className="flex justify-start">
                        <StatusBadgeWithAging status={r.status} statusAt={r.status_at} />
                      </div>
                    </div>

                    <div className="mt-4 grid grid-cols-2 gap-3 text-sm max-md:pr-8">
                      <div>
                        <div className="text-xs text-gray-500">Invoice date</div>
                        <div className="font-semibold text-gray-800">{formatDate(r.invoice_date)}</div>
                      </div>
                      <div>
                        <div className="text-xs text-gray-500">Due date</div>
                        <div className="font-semibold text-gray-800">{formatDate(r.due_date)}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {!loading && rows.length > 0 && (() => {
            const startItem = offset + 1;
            const endItem = offset + rows.length;
            const hasPrevPage = offset > 0;
            const hasNextPage = offset + limit < paginationTotal;
            const showPagination = totalPages > 1 || paginationTotal > 0;
            if (!showPagination) return null;
            return (
              <div className="mt-4 pt-4 border-t border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="text-sm text-gray-700 text-center md:text-left">
                  Showing {startItem} to {endItem} of {paginationTotal} invoices
                </div>
                <div className="flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => setOffset((o) => Math.max(0, o - limit))}
                    disabled={!hasPrevPage}
                    className={`px-3 py-1 text-sm rounded-lg border ${
                      hasPrevPage ? 'border-gray-300 text-gray-700 hover:bg-gray-50' : 'border-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          type="button"
                          onClick={() => setOffset((pageNum - 1) * limit)}
                          className={`px-3 py-1 text-sm rounded-lg ${
                            pageNum === page ? 'bg-[#0B4778] text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => setOffset((o) => (o + limit < paginationTotal ? o + limit : o))}
                    disabled={!hasNextPage}
                    className={`px-3 py-1 text-sm rounded-lg border ${
                      hasNextPage ? 'border-gray-300 text-gray-700 hover:bg-gray-50' : 'border-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full table-fixed border border-gray-200 border-collapse" style={{ tableLayout: 'fixed' }}>
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th
                    className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 align-top whitespace-nowrap border-r border-gray-200 last:border-r-0"
                    style={{ width: '16%' }}
                  >
                    Invoice
                  </th>
                  <th
                    className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 align-top whitespace-nowrap border-r border-gray-200 last:border-r-0"
                    style={{ width: '28%' }}
                  >
                    Customer
                  </th>
                  <th
                    className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 align-top whitespace-nowrap border-r border-gray-200 last:border-r-0"
                    style={{ width: '12%' }}
                  >
                    DUE AMOUNT
                  </th>
                  <th
                    className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 align-top whitespace-nowrap border-r border-gray-200 last:border-r-0"
                    style={{ width: '12%' }}
                  >
                    Status
                  </th>
                  <th
                    className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 align-top whitespace-nowrap border-r border-gray-200 last:border-r-0"
                    style={{ width: '12%' }}
                  >
                    Invoice date
                  </th>
                  <th
                    className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 align-top whitespace-nowrap border-r border-gray-200 last:border-r-0"
                    style={{ width: '12%' }}
                  >
                    Due date
                  </th>
                  <th
                    className="text-center text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 align-middle whitespace-nowrap border-r border-gray-200 last:border-r-0"
                    style={{ width: '12%' }}
                  >
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center text-gray-600">
                      Loading invoices…
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-10 text-center">
                      <div className="text-gray-500">
                        <FileText className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                        <div className="font-semibold text-gray-700">No invoices yet</div>
                        <div className="text-sm text-gray-500 mt-1">Create an invoice from an appointment to see it here.</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                      rows.map((r) => {
                    const fromBooking = !!r.appointment_id;
                    const isOpen = actionsMenu?.invoiceId === r.id;
                    return (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 align-top border-r border-gray-200 last:border-r-0">
                          <div className="flex items-start gap-3">
                            <div
                              className={`w-9 h-9 rounded-lg flex items-center justify-center border ${
                                r.status === 'PAID'
                                  ? 'bg-green-50 border-green-200 text-green-700'
                                  : r.status === 'SENT'
                                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                                  : 'bg-gray-50 border-gray-200 text-gray-700'
                              }`}
                            >
                              <i className={`bi ${fromBooking ? 'bi-receipt' : 'bi-file-earmark-text'}`}></i>
                            </div>
                            <div className="min-w-0 flex flex-col gap-1">
                              <InvoiceNumberLink invoiceId={r.id} className="font-semibold whitespace-nowrap" />
                              <span
                                className={`inline-flex items-center gap-1 w-fit px-2 py-0.5 rounded-full border text-xs font-semibold ${
                                  fromBooking
                                    ? 'bg-blue-50 border-blue-200 text-blue-700'
                                    : 'bg-slate-100 border-slate-200 text-slate-700'
                                }`}
                              >
                                <i className={`bi ${fromBooking ? 'bi-calendar2-check' : 'bi-pencil'}`}></i>
                                {fromBooking ? 'From booking' : 'Manual'}
                              </span>
                            </div>
                          </div>
                        </td>

                        <td className="px-4 py-3 align-top border-r border-gray-200 last:border-r-0">
                          <div className="min-w-0">
                            <div className="font-semibold text-gray-900 truncate">{r.bill_to || 'Customer'}</div>
                            <div className="text-sm text-gray-600 truncate">{r.customer_email || 'No email on file'}</div>
                          </div>
                        </td>

                        <td className="px-4 py-3 text-left align-top whitespace-nowrap border-r border-gray-200 last:border-r-0">
                          <div className="font-semibold text-gray-900">${formatMoney(r.due !== undefined && r.due !== null ? r.due : r.total)}</div>
                          <div className="text-sm text-gray-600">{r.status === 'PAID' ? 'Paid' : r.status === 'SENT' ? 'Outstanding' : 'Draft'}</div>
                        </td>

                        <td className="px-4 py-3 align-top whitespace-nowrap border-r border-gray-200 last:border-r-0">
                          <StatusBadgeWithAging status={r.status} statusAt={r.status_at} />
                        </td>

                        <td className="px-4 py-3 text-sm text-gray-700 align-top whitespace-nowrap border-r border-gray-200 last:border-r-0">{formatDate(r.invoice_date)}</td>
                        <td className="px-4 py-3 text-sm text-gray-700 align-top whitespace-nowrap border-r border-gray-200 last:border-r-0">{formatDate(r.due_date)}</td>

                        <td className="px-4 py-3 text-center align-middle border-r border-gray-200 last:border-r-0">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              const el = e.currentTarget as unknown as HTMLElement;
                              if (isOpen) setActionsMenu(null);
                              else openActionsFor(r.id, el);
                            }}
                            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded transition-colors inline-flex items-center justify-center"
                            title="Actions"
                            aria-label="Invoice actions"
                          >
                            <Settings className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {!loading && rows.length > 0 && (() => {
            const startItem = offset + 1;
            const endItem = offset + rows.length;
            const hasPrevPage = offset > 0;
            const hasNextPage = offset + limit < paginationTotal;
            const showPagination = totalPages > 1 || paginationTotal > 0;
            if (!showPagination) return null;
            return (
              <div className="px-6 py-4 border-t border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="text-sm text-gray-700 text-center md:text-left">
                  Showing {startItem} to {endItem} of {paginationTotal} invoices
                </div>
                <div className="flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => setOffset((o) => Math.max(0, o - limit))}
                    disabled={!hasPrevPage}
                    className={`px-3 py-1 text-sm rounded-lg border ${
                      hasPrevPage ? 'border-gray-300 text-gray-700 hover:bg-gray-50' : 'border-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Previous
                  </button>
                  <div className="flex items-center gap-1">
                    {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                      let pageNum: number;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (page <= 3) {
                        pageNum = i + 1;
                      } else if (page >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = page - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          type="button"
                          onClick={() => setOffset((pageNum - 1) * limit)}
                          className={`px-3 py-1 text-sm rounded-lg ${
                            pageNum === page ? 'bg-[#0B4778] text-white' : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <button
                    type="button"
                    onClick={() => setOffset((o) => (o + limit < paginationTotal ? o + limit : o))}
                    disabled={!hasNextPage}
                    className={`px-3 py-1 text-sm rounded-lg border ${
                      hasNextPage ? 'border-gray-300 text-gray-700 hover:bg-gray-50' : 'border-gray-200 text-gray-400 cursor-not-allowed'
                    }`}
                  >
                    Next
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      )}

      {/* Mobile: centered invoice actions modal */}
      {mobileActionsInvoiceId && (
        <div className="md:hidden fixed inset-0 z-[99999] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label="Invoice actions">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            onClick={() => setMobileActionsInvoiceId(null)}
            aria-label="Close invoice actions"
          />
          <div className="relative bg-white rounded-xl shadow-xl max-h-[80vh] overflow-y-auto w-full max-w-sm">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white rounded-t-xl">
              <h3 className="font-semibold text-gray-900">{toInvoiceLabel(mobileActionsInvoiceId)}</h3>
              <button
                type="button"
                onClick={() => setMobileActionsInvoiceId(null)}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="py-2 pb-6">
              {renderInvoiceActionButtons(mobileActionsInvoiceId, true)}
            </div>
          </div>
        </div>
      )}

      {/* Desktop: fixed-position actions menu to avoid clipping inside scroll containers */}
      {actionsMenu &&
        typeof document !== 'undefined' &&
        createPortal(
          <div
            ref={actionsMenuRef}
            className="hidden md:block fixed w-56 bg-white border border-gray-200 rounded-lg shadow-xl z-[99999]"
            style={{ top: actionsMenu.top, left: actionsMenu.left }}
            role="menu"
            aria-label="Invoice actions menu"
          >
            <div className="py-1">
              {renderInvoiceActionButtons(actionsMenu.invoiceId)}
            </div>
          </div>,
          document.body
        )}

      {/* Confirmation modals (match existing dashboard modal styling) */}
      <MarkInvoicePaidModal
        isOpen={!!markPaidModal?.isOpen}
        amountDue={markPaidModal?.amountDue ?? 0}
        onClose={() => setMarkPaidModal(null)}
        onSubmit={async (payment) => {
          if (!markPaidModal?.invoiceId) return;
          await markAsPaid(markPaidModal.invoiceId, payment);
        }}
      />

      <CustomAlertModal
        isOpen={confirm.isOpen && confirm.action === 'VOID'}
        onClose={() => setConfirm({ isOpen: false, action: null, invoiceId: undefined })}
        title="Void this invoice?"
        message={
          selectedInvoice
            ? `This will void ${toInvoiceLabel(selectedInvoice.id)}. A voided invoice cannot be sent.`
            : 'This will void the invoice.'
        }
        type="error"
        showCancelButton
        confirmIcon={<Ban className="w-4 h-4" />}
        confirmText="Void Invoice"
        cancelText="Cancel"
        onConfirm={() => {
          const id = confirm.invoiceId;
          setConfirm({ isOpen: false, action: null, invoiceId: undefined });
          if (id) voidInvoice(id);
        }}
      />
    </div>
  );
};

export default InvoicesSection;

